/**
 * Pure helpers for the "Another day or time" part of the invitation form:
 * which days to offer as quick choices and the From/Until 15-min grids.
 * Day/time strings are the BUILDING's wall clock (`YYYY-MM-DD`, `HH:MM`).
 */
import type { BuildingHours } from '@hub-wifi/shared'
import { addDaysInTz, todayYmdInTz } from '../../lib/calendar-time'
import { hoursForDay, hmToMinutes, minutesToHm, SLOT_MINUTES, startSlots } from '../home/book-sheet'

/** Quick day choices (tabs); a "Pick a date" option sits next to them. */
export const QUICK_DAYS = 3
const SCAN_DAYS = 14
/** Default visit length, in minutes. */
const DEFAULT_LENGTH = 60
/** The API wants `HH:MM`, so a whole-day fallback ends at 23:45, not 24:00. */
const LAST_END_MINUTES = 23 * 60 + 45

/** The next open days that still have a start time left (today included). */
export function quickDays(
  hours: BuildingHours[],
  timeZone: string,
  now: Date,
  count = QUICK_DAYS,
): string[] {
  const today = todayYmdInTz(timeZone, now)
  const out: string[] = []
  for (let i = 0; i < SCAN_DAYS && out.length < count; i++) {
    const ymd = addDaysInTz(today, i, timeZone)
    if (startSlots(hours, ymd, timeZone, now).length > 0) out.push(ymd)
  }
  return out
}

/** End-time options after `from`: 15-min grid up to closing. */
export function untilSlots(hours: BuildingHours[], ymd: string, from: string): string[] {
  const day = hoursForDay(hours, ymd)
  if (!day || !from) return []
  const last = Math.min(hmToMinutes(day.close), LAST_END_MINUTES)
  const out: string[] = []
  for (let m = hmToMinutes(from) + SLOT_MINUTES; m <= last; m += SLOT_MINUTES) {
    out.push(minutesToHm(m))
  }
  return out
}

/** One hour after `from` when offered, otherwise the latest option. */
export function defaultUntil(from: string, options: string[]): string {
  const target = minutesToHm(hmToMinutes(from) + DEFAULT_LENGTH)
  return options.includes(target) ? target : (options[options.length - 1] ?? '')
}
